/**
 * MarkUnreachableDialog.tsx — why nobody could reach this patient.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Server: clinic-service
 * `src/deals/unreachable-reason.ts` and `patchStage`.
 *
 * ⚠️ IT IS `CloseAsLostDialog`'S SHAPE, NOT `ConfirmDialog`'S, AND FOR THE SAME
 * REASON. This move has two refusals the shared confirm cannot render: the
 * server's 409 attempts gate and its 400 for a reason that needs a note.
 * `ConfirmDialog` closes on submit and leaves the caller to report the failure
 * — and the caller reports it with `setNotice`, which draws an Alert ON THE
 * BOARD, behind this drawer's fixed scrim and its `aria-modal="true"`. That is
 * exactly how "Close as Lost" came to look like a silent no-op (M via Arham,
 * 2026-09-17): the message was produced, was correct, and was invisible.
 *
 * So this dialog stays OPEN on a refusal and renders it INSIDE the panel.
 *
 * ⚠️ AND THE TONE IS NOT `danger`. Closing a deal as lost ends it; this parks a
 * card in a lane it can come back out of in one click, and the patient is still
 * a patient. `ui/ConfirmDialog`'s own rule — "`tone="danger"` is for the
 * consequence, not the importance" — puts this one on the benign primary.
 */
import { useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { Alert, Button, ErrorLine, Field, Select, TextArea } from '../ui/kit';
import {
  UNREACHABLE_REASON_OPTIONS,
  unreachableAttemptsWarning,
  unreachableReasonNeedsDetail,
  type UnreachableReasonCode,
} from './unreachableReasons';

export function MarkUnreachableDialog({
  dealName,
  stage,
  attemptsThisRound,
  busy,
  serverError,
  onConfirm,
  onCancel,
}: {
  dealName: string;
  /** The lane the card is leaving — decides whether the attempts gate applies. */
  stage: string;
  /** From the card. `null` when unknown — we do not guess; the server answers. */
  attemptsThisRound: number | null;
  busy: boolean;
  /** The SERVER's own words on a refusal. Rendered verbatim; never re-derived. */
  serverError: string | null;
  onConfirm: (reasonCode: UnreachableReasonCode, detail: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState<UnreachableReasonCode | ''>('');
  const [detail, setDetail] = useState('');

  const needsDetail = unreachableReasonNeedsDetail(code);
  const missingDetail = needsDetail && detail.trim() === '';
  const attemptsWarning = unreachableAttemptsWarning(stage, attemptsThisRound);

  return (
    <DialogShell title={`Mark ${dealName} as unreachable`} onCancel={onCancel}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gps-s-3)' }}>
        {/* What this actually does, before they choose anything. The lane is
            recoverable and the patient is not being closed — saying so stops
            this reading as a quieter "Close as lost". */}
        <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--gps-muted)' }}>
          The card moves to <strong>Unreachable — no answer</strong> and leaves the working board. It is
          not closed: if they get in touch it comes straight back.
        </p>

        <Field label="Why couldn't we reach them?" htmlFor="unreachable-reason">
          <Select
            id="unreachable-reason"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value as UnreachableReasonCode | '')}
          >
            <option value="">Choose a reason…</option>
            {UNREACHABLE_REASON_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        {/* ⚠️ ADVICE, NOT A REFUSAL, and the tone says so. They have not done
            anything wrong — the card is simply in an admin round that says
            somebody is still ringing. The way out is in the sentence, and the
            server is what actually refuses. */}
        {attemptsWarning ? (
          <Alert tone="warning" role="status">
            {attemptsWarning}
          </Alert>
        ) : null}

        <Field
          label={needsDetail ? 'What happened?' : 'Anything to add? (optional)'}
          htmlFor="unreachable-detail"
        >
          <TextArea
            id="unreachable-detail"
            rows={3}
            value={detail}
            disabled={busy}
            placeholder={needsDetail ? 'A short note — this one needs it' : 'Optional'}
            onChange={(e) => setDetail(e.target.value)}
          />
        </Field>

        {/* THE SERVER'S REFUSAL, IN THE PANEL. See the header: this is the whole
            reason the dialog is not a `ConfirmDialog`. */}
        {serverError ? (
          <div id="unreachable-server-error">
            <ErrorLine>{serverError}</ErrorLine>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            // Disabled ONLY on what this screen can prove: no reason chosen, or
            // a reason that needs a note and has none. The attempts gate is
            // deliberately NOT a block here — the count can be stale or unknown,
            // and inventing a refusal the server did not make is the failure
            // this whole mirror exists to avoid.
            disabled={busy || code === '' || missingDetail}
            aria-describedby={serverError ? 'unreachable-server-error' : undefined}
            onClick={() => code !== '' && onConfirm(code, detail.trim())}
          >
            {busy ? 'Moving…' : 'Mark unreachable'}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
